import {
  AnySchema,
  BooleanSchema,
  DateSchema,
  NumberSchema,
  StringSchema,
} from 'yup';
import { DateTime } from 'luxon';

import { i18n } from 'i18n';
import { FormField } from 'theSource/types';
import { SIN_FIELD_NAME, SSN_FIELD_NAME } from '../constants';

const isValidSsn = (value: string) => /^(\d{3}-?\d{2}-?\d{4})$/.test(value);

const isValidSin = (value: string) => /^(\d{3}-?\d{3}-?\d{3})$/.test(value);

const isEmailField = (fieldName: string) =>
  fieldName.toLowerCase().includes('email');

const startDate = DateTime.fromISO('1900-01-01T00:00:00.00');
const endDate = DateTime.fromISO('2100-01-01T00:00:00.00');

export const applyValidEmail = (schema: StringSchema, field: FormField) => {
  if (isEmailField(field.fieldName)) {
    return schema.email(i18n.t('invalidEmail'));
  }
  return schema;
};

export const applyValidSsn = (schema: StringSchema, field: FormField) => {
  if (field.fieldName === SSN_FIELD_NAME) {
    return schema.test('isValidSsn', i18n.t('invalidSsn'), (value) =>
      value ? value === field.value || isValidSsn(value) : true
    );
  }
  return schema;
};

export const applyValidSin = (schema: StringSchema, field: FormField) => {
  if (field.fieldName === SIN_FIELD_NAME) {
    return schema.test('isValidSin', i18n.t('invalidSin'), (value) =>
      value ? value === field.value || isValidSin(value) : true
    );
  }
  return schema;
};

export const applyMaxLength = (schema: StringSchema, field: FormField) => {
  if (field.maxLength) {
    return schema.max(
      field.maxLength,
      i18n.t('maximumLengthExceeded', { count: field.maxLength })
    );
  }
  return schema;
};

export const applyValidDateRange = (schema: DateSchema) => {
  return schema
    .max(
      endDate,
      i18n.t('invalidYear', {
        interpolation: { escapeValue: false },
      })
    )
    .min(
      startDate,
      i18n.t('invalidYear', {
        interpolation: { escapeValue: false },
      })
    );
};

export const applyIsRequired = (schema: AnySchema, field: FormField) => {
  if (field.required) {
    return schema.required(
      i18n.t('fieldCannotBeEmpty', {
        name: field.label,
        interpolation: { escapeValue: false },
      })
    );
  }
  return schema;
};

export const applyTrimWhitespace = (schema: StringSchema) => {
  return schema.trim();
};

export const applyIsRequiredTruthy = (schema: AnySchema, field: FormField) => {
  if (field.required) {
    return schema.oneOf(
      [true],
      i18n.t('fieldCannotBeEmpty', {
        name: 'Field',
        interpolation: { escapeValue: false },
      })
    );
  }
  return schema;
};

export const applyGeneralBooleanRules = (
  schema: BooleanSchema,
  field: FormField
): BooleanSchema => {
  return applyIsRequired(schema, field) as BooleanSchema;
};

export const applyRequiredTruthyBooleanRules = (
  schema: BooleanSchema,
  field: FormField
): BooleanSchema => {
  return applyIsRequiredTruthy(schema, field) as BooleanSchema;
};

export const applyGeneralNumberRules = (
  schema: NumberSchema,
  field: FormField
): NumberSchema => {
  return applyIsRequired(schema, field) as NumberSchema;
};

export const applyGeneralStringRules = (
  schema: StringSchema,
  field: FormField
): StringSchema => {
  schema = applyIsRequired(schema, field);
  schema = applyMaxLength(schema, field);
  schema = applyValidEmail(schema, field);
  schema = applyValidSsn(schema, field);
  schema = applyTrimWhitespace(schema);
  return applyValidSin(schema, field);
};

export const applyGeneralDateRules = (
  schema: DateSchema,
  field: FormField
): DateSchema => {
  schema = applyValidDateRange(schema);
  return applyIsRequired(schema, field);
};

export const generalValidations = (requireTruthyBooleans = false) => {
  return {
    applyBooleanRules: requireTruthyBooleans
      ? applyRequiredTruthyBooleanRules
      : applyGeneralBooleanRules,
    applyDateRules: applyGeneralDateRules,
    applyNumberRules: applyGeneralNumberRules,
    applyStringRules: applyGeneralStringRules,
  };
};
